import { useLayoutEffect, useState } from 'react'

export const useElementSize = (element: HTMLElement | null) => {
  const [size, setSize] = useState<DOMRect | null>(null)

  useLayoutEffect(() => {
    if (!element) {
      return
    }

    const rect = element.getBoundingClientRect()

    setSize(rect)

    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target !== element) {
          return
        }

        setSize(entry.contentRect)
      })
    })

    observer.observe(element)

    return () => {
      observer.disconnect()
    }
  }, [element])

  return size
}

export const useElementDimensions = (elements: Element[]) => {
  const [dimensions, setDimensions] = useState<Map<Element, DOMRect>>(new Map())

  useLayoutEffect(() => {
    const updateDimensions = (elements: Element[]) => {
      const updatedDimensions = new Map(dimensions)

      elements.forEach((element) => {
        const rect = element.getBoundingClientRect()

        updatedDimensions.set(element, rect)
      })

      setDimensions(updatedDimensions)
    }

    updateDimensions(elements)

    const observer = new ResizeObserver((entries) => {
      if (entries.length === elements.length) {
        const observedElements = entries.map((entry) => entry.target)

        updateDimensions(observedElements)
      } else {
        // Fix for ActionPopover when resizeObserver doesn't return all elements
        updateDimensions(elements)
      }
    })

    // observe all elements for size changes
    elements.forEach((element) => observer.observe(element))

    return () => {
      observer.disconnect()
    }
  }, [elements])

  return dimensions
}
